import { useState, FormEvent, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import api, { authApi } from '../lib/api';
import { useAuthStore } from '../store/useAuthStore';
import { Organization } from '../types';
import PasswordInput from '../components/shared/PasswordInput';
import ContactModal from '../components/ContactModal';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import AuthMarketingPanel from '../components/auth/AuthMarketingPanel';
import AuthFormPanel from '../components/auth/AuthFormPanel';
import PrivacyConsentCheckbox from '../components/legal/PrivacyConsentCheckbox';
import { STATUS_PAGE_URL } from '../lib/siteConfig';
import { IDLE_SIGNOUT_FLAG } from '../lib/sessionManager';

type Mode = 'login' | 'create_org' | 'join_org';

const FORM_HEADINGS: Record<Mode, { title: string; subtitle: string }> = {
  create_org: { title: 'Get started',    subtitle: 'Create your organization in under a minute.' },
  login:      { title: 'Welcome back',   subtitle: 'Sign in to your organization.' },
  join_org:   { title: 'Request access', subtitle: 'Join an existing organization. An admin will approve your request.' },
};

export default function LoginPage() {
  // Primary acquisition surface — unauthenticated visitors land here. SEO
  // metadata leads with the persona language (scrum masters / agile coaches)
  // and names the two product layers explicitly so the snippet matches the
  // searches we're targeting (CLAUDE.md Critical Rule 3 + the marketing-
  // roadmap Cluster E direction from 2026-05-26).
  useDocumentMeta({
    title: 'Sign in or create an organization',
    description:
      'Avium is agile reporting for scrum masters, agile coaches, and engineering managers using Jira. Avium Signals detects sprint risk, over-allocation, velocity drift, and blocked work. Avium Intelligence turns those signals into a plain-English daily briefing. Free tier — no credit card.',
    path: '/login',
  });

  const [mode, setMode] = useState<Mode>('create_org');

  // Login fields
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');

  // Create org fields
  const [coName, setCoName]         = useState('');
  const [coEmail, setCoEmail]       = useState('');
  const [coPassword, setCoPassword] = useState('');
  const [coConfirm, setCoConfirm]   = useState('');
  const [coOrgName, setCoOrgName]   = useState('');

  // Join org fields
  const [joName, setJoName]         = useState('');
  const [joEmail, setJoEmail]       = useState('');
  const [joPassword, setJoPassword] = useState('');
  const [joConfirm, setJoConfirm]   = useState('');
  const [joOrgId, setJoOrgId]       = useState('');
  const [orgs, setOrgs]             = useState<Organization[]>([]);
  const [orgSearch, setOrgSearch]   = useState('');
  const [pendingMessage, setPendingMessage] = useState('');

  // When set, show a "verify your email" panel instead of the forms — used both
  // after self-signup and when an unverified user tries to log in.
  const [verifyNoticeEmail, setVerifyNoticeEmail] = useState('');
  const [resending, setResending] = useState(false);

  // When set, show a modal telling the user they already have an account.
  // Fires when create-org returns 401 ("Email already registered with a
  // different password") — the user typed an email that's already on file
  // but with a different password (forgot they signed up before, etc.).
  // We route them to Sign In rather than letting them flounder in the
  // signup form. User report 2026-05-24.
  const [existingAccountEmail, setExistingAccountEmail] = useState('');

  // Privacy-policy consent — required to enable the Get Started / Request Access
  // submit. Cleared on every mode switch (resetFields) so a fresh form re-asks.
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);

  const [loading, setLoading] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = (location.state as { message?: string } | null)?.message;

  // Explain an idle-timeout bounce: sessionManager sets this flag right before
  // redirecting here, so the user knows the sign-out was deliberate security
  // behavior, not a bug. One-shot — read once, then cleared.
  const [idleSignedOut, setIdleSignedOut] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem(IDLE_SIGNOUT_FLAG) === '1') {
        sessionStorage.removeItem(IDLE_SIGNOUT_FLAG);
        setIdleSignedOut(true);
        setMode('login');
      }
    } catch { /* storage blocked — skip the notice */ }
  }, []);

  useEffect(() => {
    api.get('/setup/status').then(res => {
      if (res.data.needsSetup) navigate('/setup');
    }).catch(() => {});
  }, [navigate]);

  // Search orgs only when user has typed 3+ characters — prevents public enumeration
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (mode !== 'join_org') return;
    if (orgSearch.length < 3) { setOrgs([]); return; }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      authApi.listOrgs(orgSearch).then(res => setOrgs(res.data)).catch(() => {});
    }, 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [orgSearch, mode]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authApi.login(email, password);
      const { token, user, org, allOrgs } = res.data;
      setAuth(user, token, org, allOrgs);
      if (allOrgs && allOrgs.length > 1) {
        navigate('/select-org');
      } else {
        // Land on `/` so HomeRedirect can fork phones to /today.
        navigate('/');
      }
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { error?: string; pendingCount?: number; email?: string } } })?.response?.data;
      if (errData?.error === 'email_not_verified') {
        setVerifyNoticeEmail(errData.email || email);
      } else if (errData?.error === 'no_approved_orgs') {
        toast.error(errData.pendingCount
          ? `Your access request is pending approval (${errData.pendingCount} org${errData.pendingCount > 1 ? 's' : ''}).`
          : 'You don\'t have access to any organization yet.');
      } else {
        toast.error('Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrg = async (e: FormEvent) => {
    e.preventDefault();
    if (coPassword !== coConfirm) { toast.error('Passwords do not match'); return; }
    if (coPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!agreedPrivacy) { toast.error('Please agree to the Privacy Policy to continue'); return; }
    setLoading(true);
    try {
      // Empty orgName → server auto-generates "{FirstName}'s Workspace" for the Free tier
      const res = await authApi.createOrg(coName, coEmail, coPassword, coOrgName.trim());
      if (res.data.needsVerification) {
        setVerifyNoticeEmail(res.data.email || coEmail);
        return;
      }
      const { token, user, org, allOrgs } = res.data;
      // allOrgs is new (server commit 2026-05-24) — fall back to [org]
      // for backwards-compat with any older response shape (defensive,
      // shouldn't fire in practice).
      const orgs = (allOrgs && Array.isArray(allOrgs) && allOrgs.length > 0) ? allOrgs : [org];
      setAuth(user, token, org, orgs);
      toast.success(`Welcome to ${org.name}!`);
      // If the user just signed up under an email that already had
      // approved memberships in other orgs, route them to the selector
      // so they can pick which workspace to enter. Mirrors the login
      // flow exactly — user direction 2026-05-24: "if they have multiple
      // accounts the system should provide a dropdown and ask them to
      // select what account they would like to enter."
      if (orgs.length > 1) {
        navigate('/select-org');
      } else {
        navigate('/');
      }
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: string; message?: string } } };
      const data = e?.response?.data;
      // 401 from /auth/create-org means the email exists but the password
      // didn't match an existing user. Route to the "you already have an
      // account" modal instead of a generic toast — gives them a one-click
      // path back to Sign In with the email pre-filled. (Server logic:
      // server/src/routes/auth.ts ~line 152.)
      if (e?.response?.status === 401 && coEmail) {
        setExistingAccountEmail(coEmail);
      } else {
        toast.error(data?.message || data?.error || 'Failed to create organization.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Modal action — user confirms they want to sign in. Switch the page
  // to the Sign In tab and prefill the email so they can just enter
  // their password.
  const handleExistingAccountSignIn = () => {
    const email = existingAccountEmail;
    setExistingAccountEmail('');
    setMode('login');
    setEmail(email);
    // Don't reset other fields — switchMode() does that, but we WANT the
    // email to carry over.
  };

  const handleResendVerification = async () => {
    if (!verifyNoticeEmail) return;
    setResending(true);
    try {
      await authApi.resendVerification(verifyNoticeEmail);
      toast.success('Verification email sent — check your inbox.');
    } catch {
      toast.error('Could not resend right now. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleJoinOrg = async (e: FormEvent) => {
    e.preventDefault();
    if (joPassword !== joConfirm) { toast.error('Passwords do not match'); return; }
    if (joPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!joOrgId) { toast.error('Please select an organization'); return; }
    if (!agreedPrivacy) { toast.error('Please agree to the Privacy Policy to continue'); return; }
    setLoading(true);
    try {
      const res = await authApi.requestAccess(joName, joEmail, joPassword, joOrgId);
      if (res.data.token) {
        const { token, user, org } = res.data;
        setAuth(user, token, org, [org]);
        navigate('/');
      } else {
        setPendingMessage(res.data.message || 'Access request submitted. An admin will review it.');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Failed to submit access request.');
    } finally {
      setLoading(false);
    }
  };

  const resetFields = () => {
    setEmail(''); setPassword('');
    setCoName(''); setCoEmail(''); setCoPassword(''); setCoConfirm(''); setCoOrgName('');
    setJoName(''); setJoEmail(''); setJoPassword(''); setJoConfirm(''); setJoOrgId(''); setOrgSearch('');
    setPendingMessage('');
    setVerifyNoticeEmail('');
    setAgreedPrivacy(false);
  };

  const switchMode = (next: Mode) => { resetFields(); setMode(next); };

  // ── Shared form input styling ──────────────────────────────────────────────
  const inputClass = "w-full px-3.5 py-2.5 text-sm text-slate-900 bg-white border border-slate-200 rounded-lg transition-all focus:outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 placeholder:text-slate-400";
  const labelClass = "block text-xs font-bold text-slate-700 mb-1.5 tracking-wide";
  const primaryBtnClass = "w-full px-4 py-3 bg-brand-primary text-white border-none rounded-lg text-sm font-bold cursor-pointer transition-all hover:bg-brand-primary-dark active:translate-y-px flex items-center justify-center gap-2 mt-2.5 disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] min-h-screen">

      {/* Shared marketing panel (also used on InviteAcceptPage etc.) */}
      <AuthMarketingPanel />

      {/* ── FORM PANEL — the product ground (bg-surface) + a real app card, so
          signing in reads as a doorway INTO the product, not a third look. ── */}
      <AuthFormPanel
        footer={
          <div className="flex items-center justify-center gap-5">
            <a
              href={STATUS_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors no-underline"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
              System Status
              <span className="text-[10px]">↗</span>
            </a>
            <span className="text-slate-300">·</span>
            <button
              type="button"
              onClick={() => setShowContact(true)}
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors bg-transparent border-0 cursor-pointer p-0"
            >
              ✉ Contact us
            </button>
            <span className="text-slate-300">·</span>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors no-underline"
            >
              Privacy
            </a>
          </div>
        }
      >

          {/* Form header */}
          <div className="mb-5">
            <div className="text-xl lg:text-2xl font-extrabold text-slate-900 tracking-tight mb-1.5">
              {FORM_HEADINGS[mode].title}
            </div>
            <div className="text-sm text-slate-500">
              {FORM_HEADINGS[mode].subtitle}
            </div>
          </div>

          {/* Mode tabs (only show Get Started / Sign In; Request Access is a link) */}
          {!pendingMessage && !verifyNoticeEmail && (
            <div className="flex gap-1 bg-slate-100 p-1 rounded-[10px] mb-5">
              <ModeTab active={mode === 'create_org'} onClick={() => switchMode('create_org')}>Get Started</ModeTab>
              <ModeTab active={mode === 'login'}      onClick={() => switchMode('login')}>Sign In</ModeTab>
            </div>
          )}

          {/* Verify-email notice — shown after self-signup or when an unverified
              user tries to sign in. Replaces the forms until they verify. */}
          {verifyNoticeEmail && (
            <div className="text-center py-6">
              <div className="text-4xl mb-3">📬</div>
              <p className="text-slate-800 font-bold text-base mb-2">Verify your email</p>
              <p className="text-slate-500 text-sm mb-1">
                We sent a verification link to <span className="font-semibold text-slate-700">{verifyNoticeEmail}</span>.
              </p>
              <p className="text-slate-500 text-sm mb-5">Click it to activate your account, then sign in.</p>
              <button onClick={handleResendVerification} disabled={resending} className={primaryBtnClass}>
                {resending ? 'Sending…' : 'Resend verification email'}
              </button>
              <button onClick={() => switchMode('login')} className="mt-4 text-brand-primary-dark text-sm underline">
                Back to Sign In
              </button>
            </div>
          )}

          {/* Success message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-800 text-sm mb-4">
              {successMessage}
            </div>
          )}

          {/* Idle sign-out notice — the session timed out for security. */}
          {idleSignedOut && (
            <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 text-slate-700 text-sm mb-4">
              You were signed out after a period of inactivity to keep your account secure. Sign back in to pick up where you left off.
            </div>
          )}

          {/* ── SIGN IN form ── */}
          {mode === 'login' && !verifyNoticeEmail && (
            <form onSubmit={handleLogin}>
              <div className="mb-3">
                <label htmlFor="login-email" className={labelClass}>Email</label>
                <input id="login-email" name="email" autoComplete="username" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className={inputClass} placeholder="you@company.com" required autoFocus />
              </div>
              <div className="mb-3">
                <label htmlFor="login-password" className={labelClass}>Password</label>
                <PasswordInput id="login-password" name="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)}
                  className={inputClass} required />
              </div>
              <div className="text-right -mt-1.5 mb-2">
                <a href="/forgot-password" className="text-xs text-slate-500 hover:text-brand-primary-dark no-underline">Forgot password?</a>
              </div>
              <button type="submit" disabled={loading} className={primaryBtnClass}>
                {loading ? 'Signing in…' : <>Sign In <span className="text-base">›</span></>}
              </button>

              <Divider />

              <SecondaryLink>
                Joining an existing team?{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); switchMode('join_org'); }}>Request access ›</a>
              </SecondaryLink>
            </form>
          )}

          {/* ── GET STARTED (Create Org) form ── */}
          {mode === 'create_org' && !verifyNoticeEmail && (
            <form onSubmit={handleCreateOrg}>
              <div className="mb-3">
                <label htmlFor="co-name" className={labelClass}>Your Full Name</label>
                <input id="co-name" name="name" autoComplete="name" type="text" value={coName} onChange={e => setCoName(e.target.value)}
                  className={inputClass} placeholder="Jane Smith" required autoFocus />
              </div>
              <div className="mb-3">
                <label htmlFor="co-org" className={labelClass}>
                  Organization Name <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input id="co-org" name="organization" autoComplete="organization" type="text" value={coOrgName} onChange={e => setCoOrgName(e.target.value)}
                  className={inputClass} placeholder="Acme Corp — leave blank for a personal organization" />
              </div>
              <div className="mb-3">
                <label htmlFor="co-email" className={labelClass}>Work Email</label>
                <input id="co-email" name="email" autoComplete="email" type="email" value={coEmail} onChange={e => setCoEmail(e.target.value)}
                  className={inputClass} placeholder="jane@acme.com" required />
              </div>
              <div className="mb-3">
                <label htmlFor="co-password" className={labelClass}>Password</label>
                <PasswordInput id="co-password" name="new-password" autoComplete="new-password" value={coPassword} onChange={e => setCoPassword(e.target.value)}
                  className={inputClass} placeholder="At least 8 characters" required />
              </div>
              <div className="mb-3">
                <label htmlFor="co-confirm" className={labelClass}>Confirm Password</label>
                <PasswordInput id="co-confirm" name="confirm-password" autoComplete="new-password" value={coConfirm} onChange={e => setCoConfirm(e.target.value)}
                  className={inputClass} placeholder="Re-enter password" required />
              </div>
              <div className="mb-1">
                <PrivacyConsentCheckbox id="co-privacy" checked={agreedPrivacy} onChange={setAgreedPrivacy} />
              </div>
              <button type="submit" disabled={loading || !agreedPrivacy} className={primaryBtnClass}>
                {loading ? 'Creating…' : <>Create my organization <span className="text-base">›</span></>}
              </button>

              <Divider />

              <SecondaryLink>
                Joining an existing team?{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); switchMode('join_org'); }}>Request access ›</a>
              </SecondaryLink>
            </form>
          )}

          {/* ── REQUEST ACCESS form ── */}
          {mode === 'join_org' && !pendingMessage && !verifyNoticeEmail && (
            <form onSubmit={handleJoinOrg}>
              <div className="mb-3">
                <label htmlFor="jo-org" className={labelClass}>Organization</label>
                <input
                  id="jo-org"
                  name="organization-search"
                  type="text"
                  value={orgSearch}
                  onChange={e => setOrgSearch(e.target.value)}
                  className={inputClass + ' mb-1'}
                  placeholder="Search by name (3+ characters)…"
                />
                {orgs.length > 0 && (
                  <div className="border border-slate-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto mt-1">
                    {orgs.map(o => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => { setJoOrgId(o.id); setOrgSearch(o.name); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${joOrgId === o.id ? 'bg-brand-primary-light text-brand-primary-dark font-semibold' : 'text-slate-700'}`}
                      >
                        {o.name}
                      </button>
                    ))}
                  </div>
                )}
                {orgSearch && orgs.length === 0 && orgSearch.length >= 3 && (
                  <p className="text-xs text-slate-400 mt-1">No organizations found.</p>
                )}
                <div className="text-[11px] text-slate-400 mt-1">
                  An admin from that organization will need to approve your request.
                </div>
              </div>
              <div className="mb-3">
                <label htmlFor="jo-name" className={labelClass}>Your Full Name</label>
                <input id="jo-name" name="name" autoComplete="name" type="text" value={joName} onChange={e => setJoName(e.target.value)}
                  className={inputClass} placeholder="Jane Smith" required />
              </div>
              <div className="mb-3">
                <label htmlFor="jo-email" className={labelClass}>Work Email</label>
                <input id="jo-email" name="email" autoComplete="email" type="email" value={joEmail} onChange={e => setJoEmail(e.target.value)}
                  className={inputClass} placeholder="jane@company.com" required />
              </div>
              <div className="mb-3">
                <label htmlFor="jo-password" className={labelClass}>Password</label>
                <PasswordInput id="jo-password" name="new-password" autoComplete="new-password" value={joPassword} onChange={e => setJoPassword(e.target.value)}
                  className={inputClass} placeholder="At least 8 characters" required />
              </div>
              <div className="mb-3">
                <label htmlFor="jo-confirm" className={labelClass}>Confirm Password</label>
                <PasswordInput id="jo-confirm" name="confirm-password" autoComplete="new-password" value={joConfirm} onChange={e => setJoConfirm(e.target.value)}
                  className={inputClass} placeholder="Re-enter password" required />
              </div>
              <div className="mb-1">
                <PrivacyConsentCheckbox id="jo-privacy" checked={agreedPrivacy} onChange={setAgreedPrivacy} />
              </div>
              <button type="submit" disabled={loading || !joOrgId || !agreedPrivacy} className={primaryBtnClass}>
                {loading ? 'Submitting…' : <>Request access <span className="text-base">›</span></>}
              </button>

              <Divider />

              <SecondaryLink>
                <a href="#" onClick={(e) => { e.preventDefault(); switchMode('create_org'); }}>← Back to Get Started</a>
              </SecondaryLink>
            </form>
          )}

          {/* Pending confirmation after request */}
          {mode === 'join_org' && pendingMessage && (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">📬</div>
              <p className="text-slate-700 font-semibold text-sm mb-2">Request Submitted</p>
              <p className="text-slate-500 text-sm">{pendingMessage}</p>
              <button onClick={() => switchMode('login')} className="mt-4 text-brand-primary-dark text-sm underline">
                Back to Sign In
              </button>
            </div>
          )}

      </AuthFormPanel>
      <ContactModal open={showContact} onClose={() => setShowContact(false)} />

      {/* "You already have an account" modal — fires when create-org returns
          401 (email exists with different password). Routes the user to
          Sign In with their email pre-filled rather than letting them keep
          failing on the signup form. */}
      {existingAccountEmail && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="existing-account-title"
          onClick={() => setExistingAccountEmail('')}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="brand-header-gradient text-white px-6 py-5">
              <h2 id="existing-account-title" className="text-xl font-extrabold">
                Looks like you already have an account
              </h2>
            </div>
            <div className="px-6 py-5 text-sm text-slate-700 leading-relaxed space-y-3">
              <p>
                We found an Avium account for <strong>{existingAccountEmail}</strong>.
                Sign in to continue — your existing organizations will be waiting.
              </p>
              <p className="text-slate-500">
                Forgot your password?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setExistingAccountEmail('');
                    navigate('/forgot-password', { state: { email: existingAccountEmail } });
                  }}
                  className="text-brand-primary hover:text-brand-primary-dark font-medium underline"
                >
                  Reset it here
                </button>
                .
              </p>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setExistingAccountEmail('')}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExistingAccountSignIn}
                className="px-5 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg"
              >
                Sign in →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ModeTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-3.5 py-2.5 bg-transparent border-none text-[13px] font-bold cursor-pointer rounded-[7px] transition-all ${
        active
          ? 'bg-white text-brand-primary-dark shadow-sm'
          : 'text-slate-500 hover:text-slate-700'
      }`}
      style={active ? { backgroundColor: 'white', boxShadow: '0 1px 3px rgba(9, 30, 66, 0.10)' } : {}}
    >
      {children}
    </button>
  );
}

function Divider() {
  return (
    <div className="flex items-center gap-3 my-5 text-[11px] font-bold text-slate-400 tracking-widest uppercase">
      <span className="flex-1 h-px bg-slate-200" />
      or
      <span className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

function SecondaryLink({ children }: { children: React.ReactNode }) {
  return (
    <div className="block text-center text-[13px] text-slate-500 [&_a]:text-brand-primary-dark [&_a]:font-bold [&_a]:no-underline [&_a]:transition-colors hover:[&_a]:text-[#e8705a]">
      {children}
    </div>
  );
}
